import React, { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { KButton, KSpan } from "kahuna-base-react-components"

import { releasePlans } from "../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../constants"
import { lang } from "../../../constants/languages"
import { CoManagerReleasePlan, ReleasePlanActiveRelease } from "../../../types"
import { formatIsoDate, mapToCamelCase } from "../../../utility"
import ReleasePlanCard from "../co-manager/ReleasePlanCard"
import ReleasePlanPanel, {
  COMING_SOON_STEPS,
  STATE_COLORS,
  comingSoon,
  stepPath,
  stepState
} from "../co-manager/ReleasePlanPanel"
import ReleasePlanCalendar from "./ReleasePlanCalendar"
import "./ReleasePlanPage.css"

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

const byDate = (first: CoManagerReleasePlan, second: CoManagerReleasePlan) =>
  first.releaseDate.localeCompare(second.releaseDate)

/**
 * The release plan page under Releases. The artist sets a date, a year of dates or a release they
 * already have, and every release gets its steps worked back from the date: the cover and the song,
 * the metadata, the submit, the pitching form, the smart link, the marketing uploads and Discovery
 * Mode. Each step ticks itself off once Kahuna sees it done, and is reminded of before it is due.
 *
 * Under the plans are the active releases that have none, with what each still misses, so a release
 * put out without a plan is followed up the same way. The co-manager card and panel are the same
 * components, on the same endpoints.
 */
const ReleasePlanPage: FC = () => {
  const navigate = useNavigate()
  const words = lang.co_manager
  const stepWords = words.release_plan_steps as { [key: string]: string }
  const [plans, setPlans] = useState<CoManagerReleasePlan[]>([])
  const [activeReleases, setActiveReleases] = useState<ReleasePlanActiveRelease[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<"list" | "calendar">("list")
  const [creating, setCreating] = useState(false)
  // A new card per opening, so a saved card does not stay saved the next time it is opened.
  const [cardKey, setCardKey] = useState(0)
  const [planning, setPlanning] = useState<number | null>(null)

  const loadActive = () =>
    releasePlans("/active/", "get")
      .then((response) =>
        setActiveReleases(mapToCamelCase<{ results: ReleasePlanActiveRelease[] }>(response.data).results)
      )
      .catch(() => setActiveReleases([]))

  useEffect(() => {
    Promise.all([
      releasePlans("/", "get")
        .then((response) => setPlans(mapToCamelCase<{ results: CoManagerReleasePlan[] }>(response.data).results))
        .catch(() => setPlans([])),
      loadActive()
    ]).finally(() => setLoading(false))
  }, [])

  const onSaved = (saved: CoManagerReleasePlan[]) => {
    setPlans((previous) => [...previous, ...saved].sort(byDate))
    loadActive()
  }

  // An active release without a plan gets one straight from its row: nothing to fill in, its date stands.
  const planRelease = (release: ReleasePlanActiveRelease) => {
    setPlanning(release.id)
    releasePlans("/", "post", {
      plans: [{ release_id: release.id, release_date: release.releaseDate }],
      source: "page"
    })
      .then((response) => {
        onSaved(mapToCamelCase<{ results: CoManagerReleasePlan[] }>(response.data).results)
        toast.success(words.release_plan_page_planned, successFilledToastOptions)
      })
      .catch((failure) => {
        const messages = words.release_plan_errors as { [key: string]: string }
        const data = failure?.response?.data
        toast.error(
          fill(messages[data?.error] || messages.generic, { title: String(data?.detail?.title ?? "") }),
          errorFilledToastOptions
        )
      })
      .finally(() => setPlanning(null))
  }

  const chip = (text: string, selected: boolean, onClick: () => void, dataTestId: string) => (
    <KButton
      dataTestId={dataTestId}
      text={text}
      onClick={onClick}
      background={selected ? "#111" : "#FFF"}
      hoverBackground={selected ? "#333" : "#FAFAFA"}
      border={selected ? "1px solid #111" : "1px solid #EDEDED"}
      hoverBorder={selected ? "1px solid #333" : "1px solid #D6D6D6"}
      borderRadius={100}
      textColor={selected ? "#FFF" : "#111"}
      fontSize={13}
      fontWeight={400}
      width="auto"
      height="32px"
      padding="6px 14px"
      shadowDisabled
    />
  )

  const linkButton = (text: string, onClick: () => void, disabled = false, dataTestId?: string) => (
    <KButton
      dataTestId={dataTestId}
      text={text}
      onClick={onClick}
      disabled={disabled}
      background="transparent"
      shadowDisabled
      width="auto"
      height="auto"
      padding="0px"
      fontSize={14}
      textColor="#6B7280"
    />
  )

  const renderActiveRelease = (release: ReleasePlanActiveRelease) => (
    <div className="co-manager-plan" key={`active-release-${release.id}`}>
      <div className="co-manager-plan-head">
        <div className="co-manager-plan-head-text">
          <KSpan text={release.title} fontSize={16} lineHeight="22px" fontWeight={500} color="#111" ellipsis />
          <KSpan
            text={fill(words.release_plan_release_on, { date: formatIsoDate(release.releaseDate) })}
            fontSize={14}
            lineHeight="20px"
            color="#999"
          />
        </div>
        {linkButton(
          planning === release.id ? words.release_plan_creating : words.release_plan_page_make_plan,
          () => planRelease(release),
          planning !== null,
          "release-plan-active-plan"
        )}
      </div>
      <div className="co-manager-plan-steps">
        {release.steps.map((step) => (
          <div className="co-manager-plan-step" key={`active-release-${release.id}-${step.key}`}>
            <div className="co-manager-status-dot" style={{ backgroundColor: STATE_COLORS[stepState(step)] }} />
            <div className="co-manager-plan-step-text">
              <KSpan text={stepWords[step.key] || step.key} fontSize={15} lineHeight="20px" color="#111" />
              <KSpan
                text={fill(words.release_plan_due, { date: formatIsoDate(step.dueDate) })}
                fontSize={13}
                lineHeight="18px"
                color={stepState(step) === "overdue" ? "#FF5C5C" : "#999"}
              />
              {!COMING_SOON_STEPS.includes(step.key) &&
                !!(words.release_plan_step_hints as { [key: string]: string })[step.key] && (
                  <KSpan
                    text={(words.release_plan_step_hints as { [key: string]: string })[step.key]}
                    fontSize={13}
                    lineHeight="18px"
                    color="#999"
                  />
                )}
            </div>
            {COMING_SOON_STEPS.includes(step.key)
              ? comingSoon(14)
              : linkButton(words.release_plan_open, () =>
                  navigate(stepPath(step.key, release.id, release.releaseStatus))
                )}
          </div>
        ))}
      </div>
    </div>
  )

  // The three things read first on the page: the steps that are late, the steps coming due, and what
  // comes out next. Counted the way the steps are shown, so a tile never says more than the list is red for.
  const countSteps = (state: "overdue" | "due_soon") =>
    plans.reduce((total, plan) => total + plan.steps.filter((step) => stepState(step) === state).length, 0)
  const overdueSteps = countSteps("overdue")
  const dueSoonSteps = countSteps("due_soon")
  const today = new Date().toISOString().slice(0, 10)
  const nextRelease = [...plans].sort(byDate).find((plan) => plan.releaseDate >= today)

  const summaryTile = (label: string, value: string, detail?: string, tone?: "alert" | "warning") => (
    <div className={tone ? `release-plan-summary-tile is-${tone}` : "release-plan-summary-tile"}>
      <KSpan text={label} fontSize={12} lineHeight="16px" color="#999" />
      <KSpan
        text={value}
        fontSize={20}
        lineHeight="28px"
        fontWeight={500}
        color={tone === "alert" ? "#D93636" : tone === "warning" ? "#E0701F" : "#111"}
        ellipsis
      />
      {!!detail && <KSpan text={detail} fontSize={12} lineHeight="16px" color="#999" ellipsis />}
    </div>
  )

  return (
    <div className="release-plan-page pb-6">
      <div className="release-plan-page-head">
        <div className="release-plan-page-title">
          <KSpan text={words.release_plan_page_title} fontSize={24} lineHeight="32px" fontWeight={500} color="#111" />
          <KSpan text={words.release_plan_page_subtitle} fontSize={14} lineHeight="20px" color="#666" />
        </div>
        <KButton
          dataTestId="release-plan-new"
          text={creating ? words.release_plan_page_close_new : words.release_plan_page_new}
          onClick={() => {
            setCreating(!creating)
            setCardKey((key) => key + 1)
          }}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="36px"
          padding="8px 16px"
          borderRadius={100}
          fontSize={14}
        />
      </div>

      {creating && (
        <div className="release-plan-page-card">
          <ReleasePlanCard key={`release-plan-card-${cardKey}`} words={words} onSaved={onSaved} source="page" />
        </div>
      )}

      {!loading && !!plans.length && (
        <div className="release-plan-summary">
          {summaryTile(
            words.release_plan_summary_overdue,
            String(overdueSteps),
            undefined,
            overdueSteps ? "alert" : undefined
          )}
          {summaryTile(
            words.release_plan_summary_due_soon,
            String(dueSoonSteps),
            words.release_plan_summary_due_soon_detail,
            dueSoonSteps ? "warning" : undefined
          )}
          {summaryTile(
            words.release_plan_summary_next,
            nextRelease ? nextRelease.title : words.release_plan_summary_none,
            nextRelease ? formatIsoDate(nextRelease.releaseDate) : undefined
          )}
        </div>
      )}

      <div className="release-plan-page-tabs">
        {chip(words.release_plan_page_list, view === "list", () => setView("list"), "release-plan-view-list")}
        {chip(
          words.release_plan_page_calendar,
          view === "calendar",
          () => setView("calendar"),
          "release-plan-view-calendar"
        )}
      </div>

      {loading ? (
        <KSpan text={words.release_plan_page_loading} fontSize={13} color="#999" />
      ) : view === "calendar" ? (
        <ReleasePlanCalendar plans={plans} activeReleases={activeReleases} />
      ) : (
        <>
          <div className="release-plan-page-section">
            <KSpan
              text={words.release_plans_title.toLocaleUpperCase()}
              fontSize={11}
              lineHeight="14px"
              fontWeight={500}
              letterSpacing="0.66px"
              color="#999"
            />
            {plans.length ? (
              <ReleasePlanPanel plans={plans} onChange={setPlans} variant="page" />
            ) : (
              <KSpan text={words.release_plan_page_empty} fontSize={14} lineHeight="20px" color="#666" />
            )}
          </div>

          {!!activeReleases.length && (
            <div className="release-plan-page-section">
              <KSpan
                text={words.release_plan_page_active_title.toLocaleUpperCase()}
                fontSize={11}
                lineHeight="14px"
                fontWeight={500}
                letterSpacing="0.66px"
                color="#999"
              />
              <KSpan text={words.release_plan_page_active_hint} fontSize={14} lineHeight="20px" color="#666" />
              {activeReleases.map(renderActiveRelease)}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ReleasePlanPage
